import { Injectable, InjectionToken, inject } from '@angular/core';
import { Observable, delay, of, throwError } from 'rxjs';
import { ReviewCaseRepository } from '../../core/review/review-case.repository';
import { REVIEW_DOCUMENT_SOURCES } from '../../core/review/review-document-fixtures';
import { ReviewScenarioService } from '../../core/review/review-scenario.service';
import { ReviewerAcceptedCase, ReviewerAssignmentSummary, ReviewerCaseView, ReviewerDeterminationDraft, ReviewerMutationResult } from './reviewer.models';

export interface ReviewerCaseService {
  listAssignments(): Observable<readonly ReviewerAssignmentSummary[]>;
  getCase(caseNumber: string): Observable<ReviewerCaseView | null>;
  acceptAssignment(caseNumber: string, attestation: boolean, expectedRevision: number): Observable<ReviewerMutationResult<ReviewerCaseView>>;
  declineAssignment(caseNumber: string, reason: string, explanation: string, expectedRevision: number): Observable<ReviewerMutationResult<null>>;
  saveDeterminationDraft(caseNumber: string, draft: ReviewerDeterminationDraft, expectedRevision: number): Observable<ReviewerMutationResult<ReviewerAcceptedCase>>;
  markPreviewed(caseNumber: string, expectedRevision: number): Observable<ReviewerMutationResult<ReviewerAcceptedCase>>;
  submitDetermination(caseNumber: string, typedName: string, attestations: readonly boolean[], expectedRevision: number): Observable<ReviewerMutationResult<ReviewerAcceptedCase>>;
  /** Streams a document's real bytes, once the assignment is accepted. */
  openDocument(caseNumber: string, documentId: string): Observable<ReviewerMutationResult<Blob>>;
}

@Injectable({ providedIn: 'root' })
export class MockReviewerCaseService implements ReviewerCaseService {
  private readonly repository = inject(ReviewCaseRepository);
  private readonly scenarios = inject(ReviewScenarioService);

  listAssignments(): Observable<readonly ReviewerAssignmentSummary[]> {
    if (this.scenarios.selectedId() === 'failures') return throwError(() => new Error('Synthetic Reviewer list failure'));
    const order = ['Overdue acceptance', 'Needs acceptance', 'In progress', 'Completed'];
    const rows = this.repository
      .list()
      .filter((item) => item.assignmentOwnerId === 'reviewer-a' && !['Declined', 'Revoked'].includes(item.assignmentState))
      .sort((a, b) => order.indexOf(a.assignmentState) - order.indexOf(b.assignmentState) || a.urgency - b.urgency)
      .map((item) => ({
        forthrightCaseNumber: item.forthrightCaseNumber,
        cmsDisputeNumber: item.cmsDisputeNumber,
        status: item.assignmentState as ReviewerAssignmentSummary['status'],
        dueLabel: item.assignmentDueLabel,
        lineItemCount: item.lineItems.length,
      }));
    return of(rows).pipe(delay(50));
  }

  getCase(caseNumber: string): Observable<ReviewerCaseView | null> {
    if (this.scenarios.selectedId() === 'failures') return throwError(() => new Error('Synthetic Reviewer detail failure'));
    return of(this.project(caseNumber)).pipe(delay(50));
  }

  acceptAssignment(caseNumber: string, attestation: boolean, expectedRevision: number): Observable<ReviewerMutationResult<ReviewerCaseView>> {
    if (!attestation) return of({ ok: false, message: 'You must confirm your independence and availability.' });
    const current = this.repository.get(caseNumber);
    if (!current || current.assignmentOwnerId !== 'reviewer-a' || !['Needs acceptance', 'Overdue acceptance'].includes(current.assignmentState))
      return of({ ok: false, message: 'This assignment is no longer available for acceptance.' });
    const updated = this.repository.update(caseNumber, expectedRevision, (item) => {
      if (!['Needs acceptance', 'Overdue acceptance'].includes(item.assignmentState)) return;
      item.assignmentState = 'In progress';
      item.determination = { choices: {}, rationale: '', revision: 1, previewedRevision: null };
    });
    return of(updated ? { ok: true, value: this.project(caseNumber)! } : { ok: false, message: 'This assignment changed. Refresh and try again.' }).pipe(delay(50));
  }

  declineAssignment(caseNumber: string, reason: string, explanation: string, expectedRevision: number): Observable<ReviewerMutationResult<null>> {
    if (!reason.trim() || !explanation.trim()) return of({ ok: false, message: 'Choose a reason and add an explanation.' });
    const current = this.repository.get(caseNumber);
    if (!current || current.assignmentOwnerId !== 'reviewer-a' || !['Needs acceptance', 'Overdue acceptance'].includes(current.assignmentState))
      return of({ ok: false, message: 'This assignment is no longer available to decline.' });
    const updated = this.repository.update(caseNumber, expectedRevision, (item) => {
      item.assignmentState = 'Declined';
      item.declinedReviewerIds.push('reviewer-a');
    });
    return of(updated ? { ok: true, value: null } : { ok: false, message: 'This assignment changed. Refresh and try again.' }).pipe(delay(50));
  }

  saveDeterminationDraft(caseNumber: string, draft: ReviewerDeterminationDraft, expectedRevision: number): Observable<ReviewerMutationResult<ReviewerAcceptedCase>> {
    const current = this.repository.get(caseNumber);
    if (!current || current.assignmentOwnerId !== 'reviewer-a' || current.assignmentState !== 'In progress') return of({ ok: false, message: 'This assignment is not available for draft changes.' });
    const updated = this.repository.update(caseNumber, expectedRevision, (item) => {
      if (item.assignmentState !== 'In progress') return;
      item.determination = { choices: { ...draft.choices }, rationale: draft.rationale, revision: (item.determination?.revision ?? 0) + 1, previewedRevision: null };
    });
    const value = updated ? this.project(caseNumber) : null;
    return of(value?.kind === 'accepted' ? { ok: true, value } : { ok: false, message: 'The draft could not be saved because the assignment changed.' }).pipe(delay(50));
  }

  markPreviewed(caseNumber: string, expectedRevision: number): Observable<ReviewerMutationResult<ReviewerAcceptedCase>> {
    const current = this.repository.get(caseNumber);
    if (!current || current.assignmentOwnerId !== 'reviewer-a' || current.assignmentState !== 'In progress') return of({ ok: false, message: 'This assignment is not available for preview.' });
    const updated = this.repository.update(caseNumber, expectedRevision, (item) => {
      if (item.determination) item.determination.previewedRevision = item.determination.revision;
    });
    const value = updated ? this.project(caseNumber) : null;
    return of(value?.kind === 'accepted' ? { ok: true, value } : { ok: false, message: 'Save the current draft before previewing.' }).pipe(delay(25));
  }

  submitDetermination(caseNumber: string, typedName: string, attestations: readonly boolean[], expectedRevision: number): Observable<ReviewerMutationResult<ReviewerAcceptedCase>> {
    if (!typedName.trim() || attestations.some((value) => !value)) return of({ ok: false, message: 'Enter your name and complete every attestation.' });
    const current = this.repository.get(caseNumber);
    if (current?.assignmentOwnerId !== 'reviewer-a') return of({ ok: false, message: 'This assignment is not assigned to the current Reviewer.' });
    if (current?.assignmentState === 'Completed') return of({ ok: true, value: this.project(caseNumber) as ReviewerAcceptedCase });
    if (!current?.determination || current.determination.previewedRevision !== current.determination.revision || !this.isComplete(current.lineItems, current.determination.choices))
      return of({ ok: false, message: 'Complete, save, and preview every eligible line item before submitting.' });
    const updated = this.repository.update(caseNumber, expectedRevision, (item) => {
      item.assignmentState = 'Completed';
      item.completedAt = 'September 14, 2026 at 11:30 AM ET';
    });
    const value = updated ? this.project(caseNumber) : null;
    return of(value?.kind === 'completed' ? { ok: true, value } : { ok: false, message: 'The assignment changed before submission. Refresh and try again.' }).pipe(delay(50));
  }

  openDocument(caseNumber: string, documentId: string): Observable<ReviewerMutationResult<Blob>> {
    const item = this.repository.get(caseNumber);
    if (!item || item.assignmentOwnerId !== 'reviewer-a' || !['In progress', 'Completed'].includes(item.assignmentState))
      return of({ ok: false, message: 'Accept the assignment before viewing documents.' });
    const descriptor = item.documents.find((document) => document.id === documentId);
    const source = REVIEW_DOCUMENT_SOURCES.find((candidate) => candidate.id === descriptor?.sourceId);
    // The fixture's synthetic text, handed back as a Blob so the review build takes the same code path
    // the real one does rather than a parallel branch that could rot.
    return of(source ? { ok: true, value: new Blob([source.text], { type: source.mediaType }) } : { ok: false, message: 'A preview is not available for this document.' });
  }

  private project(caseNumber: string): ReviewerCaseView | null {
    const item = this.repository.get(caseNumber);
    if (!item || item.assignmentOwnerId !== 'reviewer-a' || ['Declined', 'Revoked'].includes(item.assignmentState)) return null;
    if (item.assignmentState === 'Needs acceptance' || item.assignmentState === 'Overdue acceptance')
      return {
        kind: 'pre-acceptance',
        forthrightCaseNumber: item.forthrightCaseNumber,
        cmsDisputeNumber: item.cmsDisputeNumber,
        status: item.assignmentState,
        dueLabel: item.assignmentDueLabel,
        revision: item.revision,
        serviceSummary: `${item.lineItems.length} ${item.lineItems.length === 1 ? 'line item' : 'line items'} awaiting assignment acceptance`,
      };
    return {
      kind: item.assignmentState === 'Completed' ? 'completed' : 'accepted',
      forthrightCaseNumber: item.forthrightCaseNumber,
      cmsDisputeNumber: item.cmsDisputeNumber,
      status: item.assignmentState as 'In progress' | 'Completed',
      dueLabel: item.assignmentDueLabel,
      revision: item.revision,
      lineItems: item.lineItems.map((line) => ({
        id: line.id,
        serviceCode: line.serviceCode,
        serviceDescription: line.serviceDescription,
        serviceDate: line.serviceDate,
        eligibleOfferIds: line.offers.filter((offer) => offer.submitted && offer.amountCents !== null).map((offer) => offer.id),
        offers: line.offers
          .map((offer, index) => ({ offer, index }))
          .filter(({ offer }) => offer.submitted)
          .map(({ offer, index }) => ({
            id: offer.id,
            partyLabel: index === 0 ? 'Provider' : 'Plan',
            amount: money(offer.amountCents),
            qpa: money(line.qpaCents),
            percentageOfQpa: percentage(offer.amountCents, line.qpaCents),
            narrative: offer.narrative,
            attributes: { ...offer.attributes },
          })),
      })),
      documents: item.documents.map(({ id, fileName, sizeBytes, mediaType }) => ({ id, fileName, sizeBytes, mediaType })),
      determination: item.determination ? { ...item.determination, choices: { ...item.determination.choices } } : { choices: {}, rationale: '', revision: 1, previewedRevision: null },
      completedAt: item.completedAt,
    };
  }

  private isComplete(
    lines: readonly { readonly id: string; readonly offers: readonly { readonly id: string; readonly submitted: boolean; readonly amountCents: number | null }[] }[],
    choices: Readonly<Record<string, string>>,
  ): boolean {
    return lines.every((line) => {
      const eligible = line.offers.filter((offer) => offer.submitted && offer.amountCents !== null).map((offer) => offer.id);
      return eligible.length > 0 && eligible.includes(choices[line.id]);
    });
  }
}

function money(cents: number | null): string | null {
  return cents === null ? null : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}
function percentage(amount: number | null, qpa: number | null): string | null {
  return amount === null || !qpa ? null : `${Math.round((amount / qpa) * 10_000) / 100}%`;
}

export const REVIEWER_CASE_SERVICE = new InjectionToken<ReviewerCaseService>('ReviewerCaseService');
